import React, { useEffect, useRef, useState } from "react";
import { pick } from "@/lib/languages";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import MfaChallengeForm from "@/components/auth/MfaChallengeForm";
import { authCallbackL as L } from "@/i18n/auth";

export default function AuthCallback() {
  const { lang } = useSettings();
  const l = pick(L, lang);
  const nav = useNavigate();
  const location = useLocation();
  const { loginWithGoogleSession, completeMfa } = useAuth();
  const processed = useRef(false);
  const active = useRef(false);
  const inFlight = useRef(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/"); return; }
    const session_id = m[1];

    // Share the auth revision guard; do not replay a one-use exchange during
    // StrictMode's effect check. Its active lifetime is restored by the effect above.
    loginWithGoogleSession(session_id, () => active.current)
      .then((data) => {
        if (!active.current) return;
        // Clean hash
        window.history.replaceState(null, "", window.location.pathname);
        if (data.mfa_required) {
          setChallenge(data.challenge);
          return;
        }
        toast.success(l.ok);
        nav("/", { replace: true, state: { user: data.user } });
      })
      .catch((error) => {
        if (!active.current || error?.code === "AUTH_SUPERSEDED") return;
        toast.error(l.failed);
        nav("/login");
      });
  }, []); // eslint-disable-line

  const submitCode = async (event) => {
    event.preventDefault();
    if (inFlight.current || !challenge) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await completeMfa(challenge, code.trim(), () => active.current);
      if (!active.current) return;
      toast.success(l.ok);
      nav("/", { replace: true });
    } catch (error) {
      if (!active.current || error?.code === "AUTH_SUPERSEDED") return;
      toast.error(l.failed);
      nav("/login", { replace: true });
    } finally {
      inFlight.current = false;
      if (active.current) {
        setBusy(false);
        setCode("");
      }
    }
  };

  if (challenge) {
    return <div className="min-h-[60vh] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-xl border border-touki bg-white p-6">
        <MfaChallengeForm lang={lang} code={code} onCodeChange={setCode} onSubmit={submitCode}
          onCancel={() => nav("/login", { replace: true })} busy={busy} idPrefix="callback-mfa" />
      </div>
    </div>;
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-touki-muted">{l.busy}</div>
    </div>
  );
}
